import styles from './Argument.module.css';
import { FlipCard, useBeat, useReducedMotionAware } from './FlipCard.js';

/**
 * The retro half of the page's argument. Same object, same mechanic: four
 * people, four honest reads, staged two ways. On the left, feedback is
 * attributed and visible the moment it's typed, so early wording sets the
 * tone. On the right, the same four write face-down; only on reveal does the
 * real spread show up, including two notes similar enough to fold into one.
 */

interface Seat {
  readonly name: string;
  /** What this person actually wrote, before anyone else could see it. */
  readonly honest: string;
  /** The one whose note lands first and shapes what follows. */
  readonly speaksFirst?: boolean;
  /** Echoes the first note closely enough to read as agreement, not signal. */
  readonly echoes?: boolean;
}

const SEATS: readonly Seat[] = [
  { name: 'Mara', honest: 'Standups ran long', speaksFirst: true },
  { name: 'Dev', honest: 'Standups ran long', echoes: true },
  { name: 'Iris', honest: 'Handoffs lack context' },
  { name: 'Tom', honest: 'Standups ran long', echoes: true },
];

const GROUPED = new Set(['Mara', 'Dev', 'Tom']);

export function RetroArgument() {
  const animate = useReducedMotionAware();
  const beat = useBeat(animate);
  const shown = beat === 'shown';

  return (
    <div className={styles.argument}>
      <section className={styles.pane}>
        <h2 className={styles.paneLabel}>Feedback with names on it</h2>

        <div className={styles.seats}>
          {SEATS.map((seat, index) => (
            <span key={seat.name} className={styles.seat}>
              <FlipCard
                front={seat.honest}
                back={seat.honest}
                flipped={false}
                delayMs={seat.speaksFirst ? 0 : 220 + index * 160}
                text
              />
              <span className={styles.seatName}>{seat.name}</span>
              <span className={styles.seatNote}>
                {seat.speaksFirst
                  ? 'posts first'
                  : seat.echoes
                    ? 'agrees'
                    : 'differs'}
              </span>
            </span>
          ))}
        </div>

        <p className={styles.paneCopy}>
          The first note sets the tone and the board fills in around it. What
          reaches the retro doc reads like consensus &mdash; because disagreeing
          with a note that already has a name on it is its own kind of hard.
        </p>
      </section>

      <div className={styles.divide} aria-hidden="true" />

      <section className={styles.pane}>
        <h2 className={styles.paneLabel}>Feedback face-down</h2>

        <div className={styles.seats}>
          {SEATS.map((seat, index) => (
            <span key={seat.name} className={styles.seat}>
              <FlipCard
                front=""
                back={seat.honest}
                flipped={shown}
                delayMs={index * 110}
                facedownFront
                text
                {...(GROUPED.has(seat.name)
                  ? { tone: 'grouped' as const }
                  : {})}
              />
              <span className={styles.seatName}>{seat.name}</span>
              <span className={styles.seatNote}>
                {shown
                  ? GROUPED.has(seat.name)
                    ? 'grouped'
                    : 'played'
                  : 'ready'}
              </span>
            </span>
          ))}
        </div>

        <p className={styles.paneCopy}>
          Everyone writes first, then the host reveals all four at once.{' '}
          <strong>Three independent notes</strong> landing on the same theme
          fold into one card &mdash; a real pattern, not the first person to
          speak up.
        </p>
      </section>
    </div>
  );
}
